import { FC, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { signIn } from 'store/auth/actions'
import { getCookie } from 'libraries/cookie'
import { RoutesWrapper } from 'libraries/router'
import { Footer, Header } from 'components/molecules'
import { getNewProducts, getProducts } from 'store/product/actions'
import { useAppDispatch, useAppSelector } from 'libraries/redux'
import { getInformations } from 'store/information/actions'
import { getStore, getCategories } from 'store/category/actions'
import { CategoriesSelectors } from 'store/category/selectors'
import { SupplierContext } from 'context/SupplierContext'
import GlobalSearch from 'features/Header/ui/Search/GlobalSearch'

import styles from './PageLayout.module.scss'
import LoadingScreen from 'shared/ui/LoaderPage'

const ScrollToTop = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }, [location])

  return null
}

const PageLayout: FC = () => {
  const dispatch = useAppDispatch()
  const token = getCookie('token')
  const { data: storeInfo } = useAppSelector(CategoriesSelectors.storeInfo)
  const { is_supplier } = useContext(SupplierContext)

  const [search, setSearch] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const navigate = useNavigate()

  const [flags, setFlags] = useState({
    storeLoaded: false,
    productsLoaded: false,
    wishlistLoaded: false,
  })

  const [fadeOut, setFadeOut] = useState(false)
  const [loadingFinished, setLoadingFinished] = useState(false)

  const trackFbqEvent = (event: 'Contact' | 'Search') => {
    if (storeInfo?.fb_pixel_id) {
      if (typeof window.fbq === 'function') {
        window.fbq('track', event)
      }
    }
  }

  const trackTTqEvent = (event: 'Search') => {
    if (storeInfo?.tt_pixel_id) {
      ;(window as any).ttq.track(event, {
        value: 0,
        currency: 'UAH',
        content_type: 'product',
        content_ids: [],
        content_name: searchValue,
        content_category: '',
        search_string: searchValue,
      })
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    trackTTqEvent('Search')
    trackFbqEvent('Search')
    if (searchValue.trim().length) {
      navigate(`/products?search=${searchValue}`)
    }
  }

  // Запускаем эффекты
  useEffect(() => {
    dispatch(getStore()).finally(() => setFlags(prev => ({ ...prev, storeLoaded: true })))

    if (!token) {
      dispatch(signIn())
    }

    dispatch(getInformations()).finally(() => setFlags(prev => ({ ...prev, wishlistLoaded: true })))

      dispatch(getCategories(window.config.CATEGORY_ID)).finally(() =>
        setFlags(prev => ({ ...prev, categoriesLoaded: true }))
      )
      dispatch(
        getProducts({
          page: 1,
          per_page: 25,
          core_filter: '',
          filter_has_special: false,
          category_filter: [Number(window.config.CATEGORY_ID)],
          price_to: '',
          price_from: '',
        })
      ).finally(() => setFlags(prev => ({ ...prev, productsLoaded: true })))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (storeInfo?.category?.id) {
      dispatch(getNewProducts(storeInfo.category.id))
    } else {
      dispatch(getNewProducts(window.config.CATEGORY_ID))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeInfo])

  useEffect(() => {
    const allReady = Object.values(flags).every(Boolean)
    if (allReady) {
      setTimeout(() => setFadeOut(true), 400)
      setTimeout(() => setLoadingFinished(true), 1000)
    }
  }, [flags])

  useEffect(() => {
    const descriptionMetaTag = document.querySelector('meta[name="description"]')
    if (descriptionMetaTag) {
      descriptionMetaTag.setAttribute('content', `Интернет магазин ${storeInfo?.name} `)
    }
    if (storeInfo?.fb_pixel_id) {
      const fbScript = document.createElement('script')

      fbScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', ${storeInfo.fb_pixel_id});
      fbq('track', 'PageView');
    `

      const noscriptImg = document.createElement('noscript')
      noscriptImg.innerHTML = `
      <img height="1" width="1" style="display:none" 
           src="https://www.facebook.com/tr?id=${storeInfo.fb_pixel_id}&ev=PageView&noscript=1"/>
    `

      document.head.appendChild(fbScript)
      document.body.appendChild(noscriptImg)
    }

    if (storeInfo?.ga_tracking_id) {
      const gmScript = document.createElement('script')

      gmScript.src = `https://www.googletagmanager.com/gtag/js?id=${storeInfo?.ga_tracking_id}`

      gmScript.async = true

      document.head.appendChild(gmScript)

      const gaScript = document.createElement('script')

      gaScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag() {window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${storeInfo.ga_tracking_id}', { send_page_view: false });
`

      document.head.appendChild(gaScript)
    }
  }, [storeInfo])

  useEffect(() => {
    if (!storeInfo?.tt_pixel_id) return

    if (window.ttq) return

    const ttPixelScript = document.createElement('script')
    ttPixelScript.innerHTML = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        var ttq = w[t] = w[t] || [];
        ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
        ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function (t) {
          var e = ttq._i[t] || [];
          for (var n = 0; n < ttq.methods.length; n++)ttq.setAndDefer(e, ttq.methods[n]);
          return e
        };
        ttq.load = function (e, n) {
          var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date;
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};
          var o = document.createElement("script");
          o.type = "text/javascript";
          o.async = true;
          o.src = i + "?sdkid=" + e + "&lib=" + t;
          var a = document.getElementsByTagName("script")[0];
          a.parentNode.insertBefore(o, a)
        };

        ttq.load('${storeInfo.tt_pixel_id}');
        ttq.page();
      }(window, document, 'ttq');
    `

    document.head.appendChild(ttPixelScript)
  }, [storeInfo])

  return (
    <>
      {!loadingFinished && <LoadingScreen fadeOut={fadeOut} />}
      {loadingFinished && (
        <>
          <Header setSearch={setSearch} isSearch={search} />

          <main>
            <div className={search ? styles.searchActive : styles.searchHidden}>
              <GlobalSearch
                isSearch={search}
                setIsSearch={setSearch}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSubmit={handleSubmit}
              />
            </div>
            <ScrollToTop />
            <RoutesWrapper />
          </main>
          <Footer />
        </>
      )}
    </>
  )
}

export default PageLayout
